//#region src/core/composition/define-external-signals.ts
/**
* Behavior factory that defines a composition's **external signals**: state and context keys set and updated from
* outside the composition (typically by an adapter) that no composed behavior declares, because the behaviors that
* consult them only read them, and read them optionally.
*
* It creates no signals itself. The behavior it returns declares the keys, and `createComposition` creates their
* signals as it does for every other behavior's keys, so they also reach the composition's inferred state and context
* types. The setup does nothing.
*
* Curried so the two shapes are named explicitly while the key lists are inferred and checked against them: leaving out
* a key of `ExternalState` or `ExternalContext` is a compile error. Both shapes must name their keys and make every key
* optional, and both default to none.
*
* @example
*   ```ts
*   const externalSignals = defineExternalSignals<UserTrackSelectionState<'audio'>>()({ state: ['userAudioTrackSelection'] });
*   const behaviors = [resolvePresentation, switchAudioTrack, externalSignals] as const;
*   type State = ResolveBehaviorState<typeof behaviors>; // includes `userAudioTrackSelection`
*   ```;
*/
function defineExternalSignals() {
	return (keys) => ({
		stateKeys: keys.state ?? [],
		contextKeys: keys.context ?? [],
		setup: () => {}
	});
}
//#endregion
export { defineExternalSignals };
